/** @jsxImportSource @emotion/react */

import {
  Select,
  SelectOption,
  Textarea,
  Typography,
} from "@a_team/ui-components";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { UserReviewObject } from "models/UserReviews";
import {
  RoleEndedReason,
  RoleEndedReasonLabel,
} from "@a_team/models/dist/UserReviewObject";

interface FeedbackProps {
  review: UserReviewObject;
  isLoading: boolean;
  showPublicFeedback: boolean;
  privateFeedback: string;
  setPrivateFeedback: (feedback: string) => void;
  publicFeedback: string;
  setPublicFeedback: (feedback: string) => void;
  endedRoleReasons: RoleEndedReason[];
  setEndedRoleReasons: (reasons: RoleEndedReason[]) => void;
}

export const FEEDBACK_MIN = 20;
export const FEEDBACK_MAX = 1000;

const getCSSRules: CSSRulesResolver = ({ breakpoints, colors }) => {
  return {
    feedbackContainer: {
      display: "flex",
      flexDirection: "column",
      marginTop: 40,
      "& [class*='-control']": {
        borderColor: colors.Grey[400],
        borderRadius: 12,
        padding: "5px 10px",
      },
      "& textarea": {
        borderColor: colors.Grey[400],
        borderRadius: 12,
      },
    },
    submit: {
      padding: "10px 40px",
      width: "100%",
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: "unset",
      },
    },
    optional: {
      color: colors.Grey[500],
    },
    textarea: {
      minHeight: 120,
      padding: 24,
    },
    actions: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 32,
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        gap: 0,
      },
    },
  };
};

interface FieldProps {
  review: UserReviewObject;
  required?: boolean;
  title: string;
  placeholder?: string;
  description: string;
  value: string;
  setFeedback: (value: string) => void;
  isLoading: boolean;
}

const FeedbackField = (props: FieldProps) => {
  const styles = useCSSRulesWithTheme(getCSSRules);
  const {
    review,
    title,
    placeholder,
    description,
    setFeedback,
    value,
    isLoading,
    required,
  } = props;
  return (
    <>
      <Typography variant="textLarge" component="div" size={16} weight={"bold"}>
        {title} {!required && <span css={styles.optional}>(Optional)</span>}
      </Typography>
      <Typography
        variant="textMedium"
        weight={"regular"}
        size={14}
        color={"Grey@500"}
      >
        {description}
      </Typography>

      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <Textarea
          onChange={(e) => setFeedback(e.target.value)}
          value={value}
          disabled={!review || isLoading}
          minLength={FEEDBACK_MIN}
          maxLength={FEEDBACK_MAX}
          width={"100%"}
          size={"stretch"}
          limit={FEEDBACK_MAX}
          description={`Enter at least ${FEEDBACK_MIN} characters.`}
          css={styles.textarea}
          placeholder={placeholder ?? ""}
        />
      </div>
    </>
  );
};

export const Feedback = ({
  review,
  isLoading,
  showPublicFeedback,
  privateFeedback,
  setPrivateFeedback,
  publicFeedback,
  setPublicFeedback,
  endedRoleReasons,
  setEndedRoleReasons,
}: FeedbackProps) => {
  const styles = useCSSRulesWithTheme(getCSSRules);

  const reasonOptions = Object.keys(RoleEndedReason).map((key) => ({
    value: key,
    label: RoleEndedReasonLabel[key as RoleEndedReason],
  }));

  return (
    <div css={styles.feedbackContainer}>
      <Typography variant="textLarge" component="div" size={16} weight={"bold"}>
        Specify the reason(s) for ending the builder’s role{" "}
        <span css={styles.optional}>(Optional)</span>
      </Typography>
      <div style={{ margin: "12px 0 64px 0" }}>
        <Select
          styles={{
            option: (style) => {
              return {
                ...style,
                color: "blue",
              };
            },
          }}
          hideCounter
          onChange={(values: unknown) => {
            const reasons = (values as SelectOption[]).map((v) => v.value);
            setEndedRoleReasons(reasons as RoleEndedReason[]);
          }}
          value={endedRoleReasons.map((reason) => ({
            value: reason,
            label: RoleEndedReasonLabel[reason as RoleEndedReason],
          }))}
          isMulti={true}
          isLoading={isLoading}
          isDisabled={!review}
          options={reasonOptions}
          placeholder={
            <span
              style={{
                fontSize: 14,
              }}
            >
              {endedRoleReasons.length > 0 ? (
                <span>
                  {endedRoleReasons.length === 1
                    ? RoleEndedReasonLabel[
                        endedRoleReasons[0] as RoleEndedReason
                      ]
                    : `${endedRoleReasons.length} reasons selected`}
                </span>
              ) : (
                "Choose a reason"
              )}
            </span>
          }
        />
      </div>

      {showPublicFeedback ? (
        <FeedbackField
          title={`Write a recommendation for ${review?.toUser?.firstName}’s A.Team profile`}
          placeholder="E.g. Made a big impact from day one..."
          description={`Your recommendation will appear under your company's name and be visible on ${review?.toUser?.firstName}'s public A.Team profile.`}
          value={publicFeedback}
          setFeedback={setPublicFeedback}
          isLoading={!!isLoading}
          review={review}
        />
      ) : (
        <FeedbackField
          title={`Share a private note about ${review?.toUser?.firstName} with A.Team`}
          placeholder="E.g. Strong communicator, but didn’t have the depth of experience we were looking for in backend systems..."
          description={
            "This feedback is confidential and won’t be shared with Jared. It helps A.Team improve your future builder recommendations."
          }
          value={privateFeedback}
          setFeedback={setPrivateFeedback}
          isLoading={!!isLoading}
          review={review}
        />
      )}
    </div>
  );
};
